import React, { useState } from 'react';
import { ConfirmModal } from '../common/ConfirmModal';
import { reportsAPI } from '../../services/api';

// Reportes Excel viejos (matrices + prorrateo del módulo Cloud): ocultos, los
// reemplazó el informe de Liquidación. Cambiar a true para volver a mostrarlos.
const MOSTRAR_REPORTES = false;

// Iniciales para el avatar: primera letra del primer y del último nombre.
const iniciales = (nombre = '') => {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primera = partes[0][0];
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return (primera + ultima).toUpperCase();
};

export const Header = ({ user, onLogout, showNotification }) => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // Guarda cual de los dos reportes se esta generando ('completo' | 'detalle').
  const [descargando, setDescargando] = useState(null);

  // Descarga un reporte. El backend lo genera contra la base en el momento,
  // asi que siempre baja con los datos del dia.
  const descargarReporte = async (cual) => {
    if (descargando) return;
    setDescargando(cual);
    try {
      const respuesta = cual === 'detalle'
        ? await reportsAPI.getDetalle()
        : await reportsAPI.getCostos();

      // El nombre viene en Content-Disposition; si no llega, se arma aqui.
      const disposicion = respuesta.headers['content-disposition'] || '';
      const encontrado = disposicion.match(/filename="?([^"]+)"?/);
      const base = cual === 'detalle' ? 'CostProject_Detalle_Costos' : 'CostProject_Resumen_Costos';
      const nombre = encontrado
        ? encontrado[1]
        : `${base}_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([respuesta.data]));
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = nombre;
      document.body.appendChild(enlace);
      enlace.click();
      document.body.removeChild(enlace);
      window.URL.revokeObjectURL(url);

      showNotification?.('success', 'Reporte generado', `Se descargo ${nombre}`);
    } catch (error) {
      console.error('Error descargando el reporte de costos:', error);
      showNotification?.(
        'error',
        'No se pudo generar el reporte',
        'Revisa la conexion con el servidor e intenta de nuevo.'
      );
    } finally {
      setDescargando(null);
    }
  };

  return (
    <header className="header-bar">
      <div className="header-left">
        <div className="brand">
          <span className="brand-logo">
            {/* Mismo ícono que el login: una sola identidad visual */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="14" width="4" height="7" rx="0.5" fill="currentColor" opacity="0.4" />
              <rect x="8" y="10" width="4" height="11" rx="0.5" fill="currentColor" opacity="0.6" />
              <rect x="14" y="6" width="4" height="15" rx="0.5" fill="currentColor" opacity="0.8" />
              <polyline points="2 12 7 8 13 10 22 3" strokeWidth="2.2" />
              <polyline points="17 3 22 3 22 8" strokeWidth="2.2" />
            </svg>
          </span>
          <span className="brand-name">CostProject</span>
        </div>
      </div>
      {user && (
        <div className="header-right">
          {MOSTRAR_REPORTES && (<>
          <button
            className="btn-reporte btn-reporte--simple"
            onClick={() => descargarReporte('detalle')}
            disabled={!!descargando}
            title="Excel de una sola hoja: recurso, aplicacion, cliente y cuanto paga cada uno"
          >
            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
              <path d="M26 24v4H6v-4H4v4a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2v-4Z" />
              <path d="M26 14l-1.41-1.41L17 20.17V2h-2v18.17l-7.59-7.58L6 14l10 10 10-10Z" />
            </svg>
            {descargando === 'detalle' ? 'Generando...' : 'Detalle simple'}
          </button>
          <button
            className="btn-reporte"
            onClick={() => descargarReporte('completo')}
            disabled={!!descargando}
            title="Excel con resumen, proyectos, aplicaciones y las matrices de cada modulo"
          >
            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
              <path d="M26 24v4H6v-4H4v4a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2v-4Z" />
              <path d="M26 14l-1.41-1.41L17 20.17V2h-2v18.17l-7.59-7.58L6 14l10 10 10-10Z" />
            </svg>
            {descargando === 'completo' ? 'Generando...' : 'Reporte completo'}
          </button>
          </>)}
          <div className="user-info" title={user.name}>
            <span className="user-avatar" aria-hidden="true">{iniciales(user.name)}</span>
            <span className="user-name">{user.name}</span>
          </div>
          <button
            className="btn-logout"
            onClick={() => setShowLogoutConfirm(true)}
            aria-label="Cerrar sesión"
            data-tooltip="Cerrar sesión"
          >
            {/* Carbon: Logout 20 */}
            <svg width="20" height="20" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
              <path d="M6 30h12a2 2 0 0 0 2-2v-3h-2v3H6V4h12v3h2V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v24a2 2 0 0 0 2 2Z" />
              <path d="M20.586 20.586 24.172 17H10v-2h14.172l-3.586-3.586L22 10l6 6-6 6-1.414-1.414z" />
            </svg>
          </button>
        </div>
      )}

      {showLogoutConfirm && (
        <ConfirmModal
          title="Cerrar sesión"
          message="¿Seguro que deseas cerrar sesión?"
          confirmLabel="Cerrar sesión"
          cancelLabel="Cancelar"
          onConfirm={() => { setShowLogoutConfirm(false); onLogout(); }}
          onClose={() => setShowLogoutConfirm(false)}
        />
      )}
    </header>
  );
};
